import { useEffect, useState } from 'react';
import { useGate, useStore } from 'effector-react';
import Scrollbars from 'react-custom-scrollbars';
import type { TagLine } from 'types/dataTypes';
import { clone } from 'ramda';
import { nanoid } from 'nanoid';
import type { SortEnd } from 'react-sortable-hoc';
import Button from '@components/Button';
import { CollapseWrapper } from '@components/Collapse';
import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import Title from '@components/Title';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import { $sceneMapApi } from '@stores/dataStores';
import { $GateTagLineList } from '@stores/editingStores';
import { $editingTagLine, $editingTagLineApi } from '@stores/editingTagLine';
import { $loadedScene } from '@stores/loadedStore';
import { sendWarning } from '@components/Noti/actions';
import { MainPlayer } from '@stores/krpPlayerStores';
import arrayMove from '@utils/arrayMove';
import { PanelTagLine, type PanelTagLineProps } from '../PanelTagLine';
import { addTagLine, removeTagLine, updateTagLine } from '../krpTagLineApi';
import { SidebarTagLineItem } from './SidebarTagLineItem';

const MAX = 50;

export function SidebarTagLine() {
  const { tagLine, id } = useStore($loadedScene)!;
  const list = tagLine ?? [];
  const [panel, setPanel] = useState<{
    index: number;
    defaultValue?: TagLine;
  } | null>(null);

  useGate($GateTagLineList, list);

  const onHotspotClick = (e: { id: string }) => {
    if (e.id && e.id !== `${$editingTagLine.getState()?.tagLine.id}_title`) {
      const i = list
        .map((tagLineItem) => `${tagLineItem.id}_title`)
        .indexOf(e.id);

      if (i > -1) {
        setPanel(null);
        setPanel({ index: i, defaultValue: list[i] });
        MainPlayer.get()?.lookToHotspot({ id: list[i].id });
      }
    }
  };

  const addExistsTagLines = () => {
    for (let i = 0; i < list.length; i++) {
      const tagLineItem = list[i];
      addTagLine(
        tagLineItem,
        true,
        false,
        {
          onLineMoving,
          onLineMoveEnd,
        },
        {
          onHotspotClick,
        }
      );
    }
  };

  const removeAllTagLines = () => {
    for (let i = 0; i < list.length; i++) {
      const tagLineItem = list[i];
      if (tagLineItem != null) {
        removeTagLine(tagLineItem.id);
      }
    }
  };

  const handleConfirm: PanelTagLineProps['onConfirm'] = (
    index,
    tagLineItem
  ) => {
    updateTagLine(tagLineItem, true);
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.tagLine ??= []; // TODO
        draft.tagLine[index] = tagLineItem;
      },
    });
    $editingTagLineApi.set(null);
  };

  const handleDelete: PanelTagLineProps['onDeleteClick'] = (index: number) => {
    if (index >= list.length) {
      return;
    }
    removeTagLine(list[index].id);
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.tagLine ??= [];
        draft.tagLine.splice(index, 1);
      },
    });
    $editingTagLineApi.set(null);
  };

  const handleDuplicate = (index: number) => {
    if (list.length >= MAX) {
      sendWarning('复制标尺失败，当前场景标尺数超过上限');
      return;
    }

    const newTagLine: TagLine = clone(list[index]);
    newTagLine.id = `tl_${nanoid()}`;
    // newTagLine.startAth += 5;
    newTagLine.startAtv += 10;
    // newTagLine.endAth += 5;
    newTagLine.endAtv += 10;
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.tagLine ??= []; // TODO
        draft.tagLine.push(newTagLine);
      },
    });
    MainPlayer.get()?.lookToHotspot({ id: newTagLine.id });
  };

  const handleCancel: PanelTagLineProps['onRequestClose'] = () => {
    setPanel(null);
    $editingTagLineApi.set(null);
    addExistsTagLines();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.tagLine = arrayMove({
          array: draft.tagLine ?? [],
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  useEffect(() => {
    addExistsTagLines();

    return () => {
      removeAllTagLines();
    };
  }, [list]);

  const onLineMoving = (e: { id: string; points: any[] }) => {
    const state = tagLine?.filter((item) => item.id === e.id)[0];
    if (!state) {
      return;
    }
    const pos = MainPlayer.get()?.calcLineMiddlePoint(state.id);
    if (state.titleSetting.enableRotate === 0) {
      const rotate = MainPlayer.get()?.calcLineDegree(state.id);
      MainPlayer.get()?.updateTextHotspot({
        id: `${state.id}_title`,
        ath: pos?.ath,
        atv: pos?.atv,
        rotate,
      });
    } else {
      MainPlayer.get()?.updateTextHotspot({
        id: `${state.id}_title`,
        ath: pos?.ath,
        atv: pos?.atv,
      });
    }
  };

  const onLineMoveEnd = (e: { id: string; points: any[] }) => {
    let tagLineItem = tagLine?.filter((item) => item.id === e.id)[0];
    const index = tagLine?.map((item) => item.id).indexOf(e.id as string) ?? -1;
    if (!tagLineItem && index > -1) {
      return;
    }
    tagLineItem = JSON.parse(JSON.stringify(tagLineItem!));
    tagLineItem!.startAth = e.points[0].ath;
    tagLineItem!.startAtv = e.points[0].atv;
    tagLineItem!.endAth = e.points[1].ath;
    tagLineItem!.endAtv = e.points[1].atv;
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.tagLine ??= []; // TODO
        draft.tagLine[index] = tagLineItem!;
      },
    });
  };

  return (
    <CollapseWrapper>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <Title>标尺标注</Title>
              <Tip className="ml-10 self-end text-base">
                {`${list.length}/${MAX}`}
              </Tip>
            </div>
          }
        />
        <Button
          size="lg"
          className="mb-10 w-full"
          disabled={list.length >= MAX}
          onClick={() => setPanel({ index: list.length })}
        >
          添加标尺
        </Button>
        {list.length > 0 ? (
          <div className="relative flex-1">
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={handleSortEnd}
            >
              <Scrollbars>
                {list.map((item, i) => (
                  <SidebarTagLineItem
                    key={item.id}
                    index={i}
                    listIndex={i}
                    item={item}
                    onClick={(e) => {
                      setPanel(e);
                      MainPlayer.get()?.lookToHotspot({
                        id: e.defaultValue.id,
                      });
                    }}
                    handleDuplicate={handleDuplicate}
                    handleDelete={handleDelete}
                  />
                ))}
              </Scrollbars>
            </SortableList>
          </div>
        ) : (
          <div className="flex-1">
            <Tip className="mt-5 text-center text-base">{`每个场景最多可添加${MAX}个标尺`}</Tip>
            <Tip className="mt-5 text-center text-base">
              添加后支持上下拖拽调整标尺顺序
            </Tip>
          </div>
        )}
      </div>
      {!!panel && (
        <PanelTagLine
          index={panel.index}
          defaultValue={panel.defaultValue}
          onConfirm={handleConfirm}
          onDeleteClick={handleDelete}
          onRequestClose={handleCancel}
          // onLineMoving={onLineMoving}
          // onLineMoveEnd={onLineMoveEnd}
        />
      )}
    </CollapseWrapper>
  );
}
